/**
 * Perception — what an enemy knows, and how long it takes to know it.
 *
 * Three channels feed one model:
 *   sight    a cone with distance and peripheral falloff plus a line-of-sight
 *            ray; it fills an *awareness meter* rather than flipping a flag,
 *   sound    discrete events (gunfire, footsteps, landings, explosions) with a
 *            radius attenuated by whether a wall sits between,
 *   squad    the blackboard, so a callout counts as a weak, indirect sighting.
 *
 * The output is `awareness` (0..1), a `lastKnown` position and a `confidence`
 * in it that decays, which is what makes a search look like a search.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { castRay, newRayResult } from './Ray.js';

export const ALERT_CALM = 0;
export const ALERT_SUSPICIOUS = 1;
export const ALERT_ALERT = 2;
export const ALERT_COMBAT = 3;

const _v = new THREE.Vector3();
const _d = new THREE.Vector3();
const _eye = new THREE.Vector3();
const _tgt = new THREE.Vector3();
/**
 * One shared result for every perception cast in the build. Perception is
 * strictly serial — an agent reads the hit and is done with it before the next
 * agent's slice runs — so one pooled object replaces what used to be an
 * allocation per agent per cast, several hundred a second at sixteen agents.
 */
const _ray = newRayResult();

export class Perception {
  constructor(agent, cfg = {}) {
    this.agent = agent;

    this.fovCos = Math.cos((cfg.fov ?? 100) * 0.5 * Math.PI / 180);
    this.focusCos = Math.cos((cfg.focus ?? 42) * 0.5 * Math.PI / 180);
    this.maxRange = cfg.maxRange ?? 62;
    this.closeRange = cfg.closeRange ?? 9;
    /** Seconds of unobstructed, centred, close viewing needed to go loud. */
    this.baseAcquire = cfg.acquire ?? 0.62;

    this.awareness = 0;
    this.alert = ALERT_CALM;
    this.hasLOS = false;
    this.losPoint = new THREE.Vector3();
    this.targetDist = Infinity;

    this.lastKnown = new THREE.Vector3();
    this.lastKnownVel = new THREE.Vector3();
    this.confidence = 0;
    this.timeSinceSeen = 999;
    this.everSeen = false;

    this.lastSoundAt = 0;
    this.soundPos = new THREE.Vector3();
    this.soundStrength = 0;

    this._losToggle = 0;
  }

  reset() {
    this.awareness = 0;
    this.alert = ALERT_CALM;
    this.hasLOS = false;
    this.confidence = 0;
    this.timeSinceSeen = 999;
    this.everSeen = false;
    this.soundStrength = 0;
  }

  /**
   * @param {number} dt   seconds since this agent's last perception slice
   * @param {object} w    { physics, player, mask, time }
   */
  update(dt, w) {
    const a = this.agent;
    this.timeSinceSeen += dt;

    const player = w.player;
    let gain = 0;
    this.hasLOS = false;

    if (player && a.alive) {
      a.eyePos(_eye);
      // Alternate between the player's eye and chest so a peeking player is
      // seen, and a fully covered one is not.
      this._losToggle ^= 1;
      _tgt.copy(player.position);
      if (this._losToggle) _tgt.y -= 0.45;

      _d.copy(_tgt).sub(_eye);
      const dist = _d.length();
      this.targetDist = dist;
      if (dist < this.maxRange && dist > 1e-3) {
        _d.multiplyScalar(1 / dist);
        const facing = a.facingDot(_d);
        if (facing > this.fovCos) {
          const hit = castRay(w.physics, _eye, _d, dist - 0.12, w.mask, _ray);
          if (!hit) {
            this.hasLOS = true;
            this.losPoint.copy(_tgt);

            // --- how fast is this enemy noticing? ---
            const distF = clamp01(1.25 - dist / this.maxRange * 1.25) * 0.85 + 0.15;
            const closeF = dist < this.closeRange ? 1.6 : 1.0;
            const angF = facing > this.focusCos
              ? 1.0
              : 0.28 + 0.72 * ((facing - this.fovCos) / Math.max(1e-4, this.focusCos - this.fovCos));
            const speed = player.speed ?? 0;
            const moveF = 1 + clamp01(speed / 6) * 0.9 + (player.sprinting ? 0.35 : 0);
            const stanceF = player.crouched ? 0.62 : 1.0;
            const firingF = (w.time - (w.playerFiredAt ?? -99)) < 0.9 ? 2.2 : 1.0;
            const lightF = w.lightAt ? (0.55 + 0.9 * clamp01(w.lightAt(_tgt))) : 1.0;
            const skillF = a.difficulty.perception;

            gain = (distF * closeF * angF * moveF * stanceF * firingF * lightF * skillF)
              / Math.max(0.05, this.baseAcquire);
          }
        }
      }
    }

    if (this.hasLOS) {
      this.awareness = Math.min(1, this.awareness + gain * dt);
      if (this.awareness > 0.32) {
        this.lastKnown.copy(player.position);
        this.lastKnownVel.copy(player.velocity || _v.set(0, 0, 0));
        this.confidence = Math.max(this.confidence, this.awareness);
        this.timeSinceSeen = 0;
        this.everSeen = true;
      }
    } else {
      // decays much slower than it builds — an alerted soldier stays alerted
      const decay = this.alert >= ALERT_COMBAT ? 0.055 : 0.14;
      this.awareness = Math.max(0, this.awareness - decay * dt);
      this.confidence = Math.max(0, this.confidence - 0.075 * dt);
    }

    // sound decays independently
    if (this.soundStrength > 0) this.soundStrength = Math.max(0, this.soundStrength - 0.5 * dt);

    this._classify();
  }

  _classify() {
    const prev = this.alert;
    if (this.awareness >= 0.999 || (this.hasLOS && this.awareness > 0.85)) this.alert = ALERT_COMBAT;
    else if (this.awareness > 0.45 || this.soundStrength > 0.55) this.alert = ALERT_ALERT;
    else if (this.awareness > 0.12 || this.soundStrength > 0.15) this.alert = ALERT_SUSPICIOUS;
    else this.alert = ALERT_CALM;
    // combat is sticky while there is any residual confidence
    if (prev === ALERT_COMBAT && this.alert < ALERT_COMBAT && this.confidence > 0.25) {
      this.alert = ALERT_ALERT;
    }
  }

  /**
   * Register a sound event. Returns true if it registered.
   * @param {{position:THREE.Vector3, radius:number, loud:number, hostile:boolean}} ev
   * @param {object} w  { physics, mask, time }
   */
  hear(ev, w) {
    const a = this.agent;
    if (!a.alive) return false;
    a.eyePos(_eye);
    _d.copy(ev.position).sub(_eye);
    const dist = _d.length();
    if (dist > ev.radius) return false;

    // one ray decides whether a wall is in the way; walls cost half the radius
    let occl = 1;
    if (dist > 1.0) {
      _d.multiplyScalar(1 / dist);
      const hit = castRay(w.physics, _eye, _d, dist - 0.2, w.mask, _ray);
      if (hit) occl = 0.42;
    }
    const strength = clamp01((1 - dist / ev.radius) * (ev.loud ?? 1) * occl);
    if (strength <= 0.02) return false;

    if (strength > this.soundStrength) {
      this.soundStrength = strength;
      this.soundPos.copy(ev.position);
      this.lastSoundAt = w.time;
    }
    // hostile noise also seeds a position guess, with poor confidence
    if (ev.hostile !== false && strength > this.confidence * 0.8) {
      this.lastKnown.copy(ev.position);
      this.confidence = Math.max(this.confidence, strength * 0.7);
      this.everSeen = true;
    }
    this.awareness = Math.min(1, this.awareness + strength * 0.55);
    this._classify();
    return true;
  }

  /** Squad telepathy is deliberately weak: a callout is a hint, not a fix. */
  shareFromSquad(pos, conf, w) {
    if (conf <= this.confidence) return;
    this.lastKnown.copy(pos);
    this.confidence = Math.min(conf * 0.85, 0.9);
    this.everSeen = true;
    this.awareness = Math.max(this.awareness, Math.min(0.75, conf * 0.8));
    this._classify();
    void w;
  }

  /** Where a searcher should look, extrapolated a little along last velocity. */
  searchPoint(out) {
    out.copy(this.lastKnown);
    const lead = Math.min(2.4, this.timeSinceSeen * 0.55);
    out.x += this.lastKnownVel.x * lead * 0.35;
    out.z += this.lastKnownVel.z * lead * 0.35;
    return out;
  }
}

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
